// Coverage UI upgrade S2 — Kalshi-style presentational primitives.
// Pure (props in, JSX out), no I/O. The honesty rules live here too:
// a `coming`/`roadmap` item is NEVER styled as bettable, and a null
// readiness renders an explicit "awaiting data" — never a fabricated
// fill.

import type { BoardItem, BoardStatus } from "../coverageOverview.ts";

const mono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
};

const STATUS_META: Record<
  BoardStatus,
  { label: string; color: string; border: string }
> = {
  live: {
    label: "LIVE",
    color: "var(--sa-accent-positive, #1fbf75)",
    border: "var(--sa-accent-positive, #1fbf75)",
  },
  coming: {
    label: "COMING",
    color: "var(--sa-accent-saffron, #e0a106)",
    border: "var(--sa-accent-saffron, #e0a106)",
  },
  roadmap: {
    label: "ROADMAP",
    color: "var(--sa-fg-muted, #9a9aa2)",
    border: "var(--sa-hairline, #2a2a30)",
  },
};

export function StatusPill({ status }: { status: BoardStatus }): JSX.Element {
  const m = STATUS_META[status];
  return (
    <span
      data-testid="status-pill"
      data-status={status}
      style={{
        ...mono,
        fontSize: 10,
        letterSpacing: "0.14em",
        padding: "2px 7px",
        borderRadius: 3,
        color: m.color,
        border: `1px solid ${m.border}`,
      }}
    >
      {m.label}
    </span>
  );
}

/** Honest readiness bar. pct null ⇒ no fill, explicit "awaiting
 *  data" — we never draw a guessed trajectory. */
export function ReadinessMeter({
  pct,
  webCorpus,
  corpusMin,
}: {
  pct: number | null;
  webCorpus?: number | null;
  corpusMin?: number | null;
}): JSX.Element {
  if (pct === null) {
    return (
      <span
        data-testid="readiness-awaiting"
        style={{ ...mono, fontSize: 10.5, color: "var(--sa-fg-muted, #6a6a72)" }}
      >
        awaiting verified data
      </span>
    );
  }
  return (
    <div
      data-testid="readiness-meter"
      data-pct={pct}
      style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 120 }}
    >
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: "var(--sa-hairline, #2a2a30)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background:
              pct >= 100
                ? "var(--sa-accent-positive, #1fbf75)"
                : "var(--sa-accent-saffron, #e0a106)",
            transition: "width 600ms ease",
          }}
        />
      </div>
      <span style={{ ...mono, fontSize: 10, color: "var(--sa-fg-muted, #9a9aa2)" }}>
        corpus {webCorpus ?? "?"}/{corpusMin ?? "?"} · {pct}%
      </span>
    </div>
  );
}

export interface StatBarProps {
  readonly live: number;
  readonly coming: number;
  readonly sports: number;
  readonly phases: number;
  readonly tierLabel: string;
}

export function StatBar(p: StatBarProps): JSX.Element {
  const cell = (k: string, v: string, accent?: string): JSX.Element => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ ...mono, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--sa-fg-muted, #9a9aa2)" }}>
        {k}
      </span>
      <span style={{ ...mono, fontSize: 18, color: accent ?? "var(--sa-fg-primary, #e8e8ea)" }}>
        {v}
      </span>
    </div>
  );
  return (
    <div
      data-testid="coverage-statbar"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        display: "flex",
        gap: 28,
        flexWrap: "wrap",
        padding: "14px 18px",
        background: "var(--sa-bg-base, #0c0c0f)",
        borderBottom: "1px solid var(--sa-hairline, #2a2a30)",
      }}
    >
      {cell("Live", String(p.live), "var(--sa-accent-positive, #1fbf75)")}
      {cell("Activating", String(p.coming))}
      {cell("Sports", `${p.sports} · ${p.phases} phases`)}
      {cell("Your access", p.tierLabel, "var(--sa-accent-saffron, #e0a106)")}
    </div>
  );
}

/** The Kalshi-style card. A dormant item shows status + readiness,
 *  NEVER a bet/stake affordance (that contract is enforced here). */
export function CoverageCard({ item }: { item: BoardItem }): JSX.Element {
  return (
    <div
      data-testid={`coverage-card-${item.key}`}
      data-status={item.status}
      style={{
        border: "1px solid var(--sa-hairline, #2a2a30)",
        borderRadius: 6,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "var(--sa-bg-elevated, transparent)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusPill status={item.status} />
        <span style={{ ...mono, fontSize: 10, color: "var(--sa-fg-muted, #6a6a72)" }}>
          phase {item.phase}
        </span>
        {item.oddsSupported === false ? (
          <span style={{ ...mono, fontSize: 9.5, color: "var(--sa-fg-muted, #6a6a72)", marginLeft: "auto" }}>
            base-rate
          </span>
        ) : item.oddsSupported === true ? (
          <span style={{ ...mono, fontSize: 9.5, color: "var(--sa-fg-muted, #6a6a72)", marginLeft: "auto" }}>
            full odds
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: 15, color: "var(--sa-fg-primary, #e8e8ea)" }}>
        {item.title}
      </div>
      {item.status === "coming" ? (
        <ReadinessMeter
          pct={item.readiness}
          webCorpus={item.webCorpus}
          corpusMin={item.corpusMin}
        />
      ) : item.status === "roadmap" ? (
        <span style={{ ...mono, fontSize: 10.5, color: "var(--sa-fg-muted, #9a9aa2)" }}>
          on the roadmap — not yet calibrated
        </span>
      ) : (
        <span style={{ ...mono, fontSize: 10.5, color: "var(--sa-accent-positive, #1fbf75)" }}>
          verified &amp; live
        </span>
      )}
    </div>
  );
}
